import { useMemo, useState } from 'react';
import Combobox from './Combobox';
import { QuickClientForm, QuickStoreForm } from './QuickCreate';

/**
 * Client (organization) autocomplete with "+ Create client".
 * onChange(id, org) · allowCreate (default true) · emptyLabel for filters.
 */
export function OrganizationPicker({ organizations, value, onChange, allowCreate = true, emptyLabel, required, placeholder = 'Type the client name…', ariaLabel = 'Client', extraOptions = [] }) {
  const [creating, setCreating] = useState(null);
  const options = useMemo(
    () => [
      ...organizations
        .map((o) => ({ value: String(o.id), label: o.name, sub: o.brand_code || '', search: `${o.email || ''} ${o.phone || ''}` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
      ...extraOptions,
    ],
    [organizations, extraOptions],
  );
  return (
    <>
      <Combobox
        options={options}
        value={value}
        onChange={onChange}
        emptyLabel={emptyLabel}
        required={required}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        createLabel="client"
        onCreate={allowCreate ? (text) => setCreating({ name: text }) : undefined}
      />
      {creating ? (
        <QuickClientForm
          initialName={creating.name}
          onClose={() => setCreating(null)}
          onCreated={(org) => {
            setCreating(null);
            onChange(String(org.id), org);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * Store (outlet) autocomplete with "+ Create store"; matches name, code, city.
 * outlets: the stores to choose from (already filtered to a client if needed).
 */
export function StorePicker({ outlets, organizations, organizationId, value, onChange, allowCreate = true, emptyLabel, required, placeholder = 'Type store name, code or city…', ariaLabel = 'Store', describe }) {
  const [creating, setCreating] = useState(null);
  const orgName = useMemo(() => new Map(organizations.map((o) => [String(o.id), o.name])), [organizations]);
  const options = useMemo(
    () =>
      outlets
        .map((o) => ({
          value: String(o.id),
          label: describe ? describe(o) : o.outlet_code ? `${o.name} (${o.outlet_code})` : o.name,
          sub: [o.city, organizationId ? '' : orgName.get(String(o.organization_id))].filter(Boolean).join(' · '),
          search: `${o.outlet_code || ''} ${o.city || ''} ${o.address || ''} ${orgName.get(String(o.organization_id)) || ''}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [outlets, orgName, organizationId, describe],
  );
  return (
    <>
      <Combobox
        options={options}
        value={value}
        onChange={onChange}
        emptyLabel={emptyLabel}
        required={required}
        placeholder={placeholder}
        ariaLabel={ariaLabel}
        createLabel="store"
        onCreate={allowCreate ? (text) => setCreating({ name: text }) : undefined}
      />
      {creating ? (
        <QuickStoreForm
          organizations={organizations}
          organizationId={organizationId}
          initialName={creating.name}
          onClose={() => setCreating(null)}
          onCreated={(outlet) => {
            setCreating(null);
            onChange(String(outlet.id), outlet);
          }}
        />
      ) : null}
    </>
  );
}
